<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4">
          <v-icon class="mr-2" color="primary">mdi-eye</v-icon>
          Announcement Views
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <div v-if="announcement" class="mb-4">
            <div class="text-body-2 text-medium-emphasis mb-2">
              <strong>Announcement:</strong>
            </div>
            <div
              class="text-body-1"
              v-html="truncateMessage(announcement.announcement_message)"
            ></div>
          </div>

          <v-skeleton-loader
            v-if="loading"
            type="table-row@5"
            class="mb-4"
          ></v-skeleton-loader>

          <div v-else-if="viewsList.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1" class="mb-4"
              >mdi-eye-off</v-icon
            >
            <p class="text-body-1 text-grey">No views found for this announcement</p>
          </div>

          <v-data-table
            v-else
            :headers="headers"
            :items="viewsList"
            :items-per-page="10"
            class="elevation-0"
            density="compact"
            :loading="loading"
            :no-data-text="'No views found'"
          >
            <template v-slot:[`item.last_viewed_on`]="{ item }">
              {{ formatTimestamp(item.last_viewed_on) }}
            </template>
            <template v-slot:[`item.user_name`]="{ item }">
              {{ item.user_name || "-" }}
            </template>
            <template v-slot:[`item.user_email_id`]="{ item }">
              {{ item.user_email_id || "-" }}
            </template>
            <template v-slot:[`item.organization_name`]="{ item }">
              {{ getOrganizationName(item.organization_id) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="handleClose"
            :disabled="loading"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { list_announcement_views, list_organizations } from "@/graphql/queries";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();

export default {
  name: "AnnouncementViewsDialog",
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    announcement: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      viewsList: [],
      headers: [
        { title: "User Name", key: "user_name", sortable: true },
        { title: "Email", key: "user_email_id", sortable: true },
        { title: "Organization", key: "organization_name", sortable: true },
        { title: "Viewed On", key: "last_viewed_on", sortable: true },
      ],
      organizationsMap: {},
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(val) {
      if (val && this.announcement) {
        this.fetchOrganizations();
        this.fetchViews();
      } else {
        this.viewsList = [];
      }
    },
    announcement: {
      handler(newVal) {
        if (newVal && this.modelValue) {
          this.fetchOrganizations();
          this.fetchViews();
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleClose() {
      this.dialog = false;
    },
    truncateMessage(message) {
      if (!message) return "";
      // Strip HTML tags for preview
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = message;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";
      // Return first 100 characters with ellipsis
      if (textContent.length > 100) {
        return textContent.substring(0, 100) + "...";
      }
      return textContent;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "-";
      const numeric = Number(timestamp);
      if (isNaN(numeric)) return timestamp;
      const ms = numeric < 1e12 ? numeric * 1000 : numeric;
      const date = new Date(ms);
      if (isNaN(date.getTime())) return timestamp;
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    getOrganizationName(organizationId) {
      if (!organizationId) return "-";
      const org = this.organizationsMap[organizationId];
      return org?.organization_name || organizationId;
    },
    async fetchOrganizations() {
      try {
        const response = await client.graphql({
          query: list_organizations,
        });

        const responseData = JSON.parse(response.data.list_organizations);
        if (responseData.status === "SUCCESS" && responseData.data) {
          // Create a map of organization_id to organization object
          this.organizationsMap = {};
          responseData.data.forEach((org) => {
            const orgId = org.organization_id || org.id;
            if (orgId) {
              this.organizationsMap[orgId] = org;
            }
          });
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        // Don't show error to user, just log it
      }
    },
    async fetchViews() {
      if (!this.announcement?.announcement_id) {
        return;
      }

      this.loading = true;
      try {
        const input = {
          announcement_id: this.announcement.announcement_id,
          announcement_source:'BMS'
        };

        const response = await client.graphql({
          query: list_announcement_views,
          variables: {
            input: input,
          },
        });

        const responseData = JSON.parse(response.data.list_announcement_views);
        // console.log(responseData);

        if (responseData.status === "SUCCESS") {
          this.viewsList = responseData.data || [];
        } else {
          this.viewsList = [];
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Failed to fetch views",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error fetching announcement views:", error);
        this.viewsList = [];
        this.snackbar = {
          show: true,
          message:
            (error.errors && error.errors[0]?.message) ||
            error.message ||
            "An error occurred while fetching views",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
</style>

